import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import { validateEmail, validatePassword, validateForm } from '../../utils/validation';
import { getLogoByDomainAndAlt } from '../../utils/logoUtils';
import { loginApi, googleAuthApi, ActiveAccountOTPFromLoginPage } from '../../services/authService';
import { usePermissions } from '../../context/PermissionsContext';

function Login() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();
  const { setPermissionsFromAPI } = usePermissions();

  useEffect(() => {
    const id = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(id);
  }, []);

  const getOrganizationUrl = () => {
    if (typeof window === 'undefined') return '';
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalhost && process.env.REACT_APP_DEFAULT_ORG_URL) {
      return process.env.REACT_APP_DEFAULT_ORG_URL;
    }
    return window.location.origin;
  };

  useEffect(() => {
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) return;

    const initGoogle = () => {
      if (!window.google?.accounts?.id) return;

      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: async (response) => {
          try {
            setIsLoading(true);
            const payload = {
              tokenId: response.credential,
              organization_url: getOrganizationUrl(),
            };
            const apiResponse = await googleAuthApi(payload);

            if (apiResponse?.success) {
              setPermissionsFromAPI(apiResponse.data.permissions || []);
              localStorage.setItem('token', apiResponse.data.access_token);
              localStorage.setItem('refresh_token', apiResponse.data.refresh_token);
              localStorage.setItem('user', JSON.stringify(apiResponse.data.user_details));
              localStorage.setItem('organization_details', JSON.stringify(apiResponse.data.dashboard));
              localStorage.setItem('role', apiResponse.data.role);

              toast.success(apiResponse.data.message || 'Login successful', {
                position: 'top-center',
                autoClose: 1000,
                className: 'bg-font-primary text-white text-sm rounded-xl shadow-lg',
                progressClassName: 'bg-primary',
                bodyClassName: 'font-medium',
                onClose: () => {
                  window.location.href = '/';
                },
              });
            } else {
              toast.error(apiResponse?.message || 'Google login failed', {
                position: 'top-center',
                autoClose: 5000,
                className: 'bg-error bg-opacity-90 text-white text-sm rounded-xl shadow-lg',
                progressClassName: 'bg-error',
                bodyClassName: 'font-medium',
              });
              setIsLoading(false);
            }
          } catch (error) {
            console.error('Google login API error:', error);
            const errorMessage =
              error?.response?.data?.message ||
              error?.response?.data?.error_msg ||
              error?.message ||
              'Google login failed';
            toast.error(errorMessage, {
              position: 'top-center',
              autoClose: 5000,
              className: 'bg-error bg-opacity-90 text-white text-sm rounded-xl shadow-lg',
              progressClassName: 'bg-error',
              bodyClassName: 'font-medium',
            });
            setIsLoading(false);
          }
        },
      });

      const el = document.getElementById('googleSignInDiv');
      if (el && !el.dataset.rendered) {
        window.google.accounts.id.renderButton(el, {
          theme: 'outline',
          size: 'large',
          width: '100%',
          text: 'continue_with',
          shape: 'rectangular',
        });
        el.dataset.rendered = 'true';
      }
    };

    if (window.google?.accounts?.id) {
      initGoogle();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initGoogle;
    document.body.appendChild(script);

    return () => {
      script.onload = null;
    };
  }, [setPermissionsFromAPI]);

  const slides = [
    {
      image: '/images/logos/loginillustration1.png',
      title: 'The Smarter Way to Manage Medical Education',
      description: 'A centralized platform for institutions to deliver structured medical courses, manage learners, and streamline clinical education programs.'
    },
    {
      image: '/images/logos/loginillustration2.png',
      title: 'The Smarter Way to Learn Clinical Medicine',
      description: 'Case-based learning, expert insights, and simplified clinical explanations delivered through an intuitive digital learning platform.'
    },
    {
      image: '/images/logos/loginillustration3.png',
      title: 'Track Learning Progress with Confidence',
      description: 'Monitor student performance, course completion, and learning engagement with powerful analytics designed for medical institutions.'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000); // slightly slower carousel
    return () => clearInterval(timer);
  }, [slides.length]);

  const validationRules = {
    email: validateEmail,
    password: validatePassword
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validation = validateForm(formData, validationRules);
    if (validation.isValid) {
      setIsLoading(true);

      const payload = {
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        organization_url: getOrganizationUrl()
      };

      try {
        const response = await loginApi(payload);

        if (response?.is_email_verified === 0) {
          try {
            await ActiveAccountOTPFromLoginPage({
              email: formData.email,
              domain: window.location.origin
            });
            toast.success('Account registered but not activated. Please check your email.', {
              position: 'top-center',
              autoClose: 2000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              className: 'bg-font-primary text-white text-sm rounded-xl shadow-lg',
              progressClassName: 'bg-primary',
              bodyClassName: 'font-medium',
              onClose: () => {
                setIsLoading(false);
                navigate('/auth/active-account', { state: { email: formData.email } });
              }
            });
          } catch (otpError) {
            console.error('Error sending OTP:', otpError);
            toast.error('Failed to send OTP. Please try again.', {
              position: 'top-center',
              autoClose: 3000,
              className: 'bg-error bg-opacity-90 text-white text-sm rounded-xl shadow-lg',
              progressClassName: 'bg-error',
              bodyClassName: 'font-medium'
            });
            setIsLoading(false);
          }
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 500));

        if (response.success) {
          setPermissionsFromAPI(response.data.permissions);
          localStorage.setItem('token', response.data.access_token);
          localStorage.setItem('refresh_token', response.data.refresh_token);
          localStorage.setItem('user', JSON.stringify(response.data.user_details));
          localStorage.setItem('organization_details', JSON.stringify(response.data.dashboard));
          localStorage.setItem('role', response.data.role);

          toast.success(response.data.message || 'Login successful', {
            position: 'top-center',
            autoClose: 1000,
            className: 'bg-font-primary text-white text-sm rounded-xl shadow-lg',
            progressClassName: 'bg-primary',
            bodyClassName: 'font-medium',
            onClose: () => {
              window.location.href = '/';
            }
          });
        } else {
          toast.error(response.message || 'Login failed', {
            position: 'top-center',
            autoClose: 5000,
            className: 'bg-error bg-opacity-90 text-white text-sm rounded-xl shadow-lg',
            progressClassName: 'bg-error',
            bodyClassName: 'font-medium'
          });
          setIsLoading(false);
        }
        window.location.reload();
      } catch (error) {
        console.error('Login API error:', error);
        await new Promise((resolve) => setTimeout(resolve, 500));

        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error_msg ||
          error?.message ||
          'Something went wrong';

        if (error?.response?.status === 404 && errorMessage.includes('User not found')) {
          toast.error('Account not found. Please register first.', {
            position: 'top-center',
            autoClose: 5000,
            className: 'bg-error bg-opacity-90 text-white text-sm rounded-xl shadow-lg',
            progressClassName: 'bg-error',
            bodyClassName: 'font-medium'
          });
        } else {
          toast.error(errorMessage, {
            position: 'top-center',
            autoClose: 5000,
            className: 'bg-error bg-opacity-90 text-white text-sm rounded-xl shadow-lg',
            progressClassName: 'bg-error',
            bodyClassName: 'font-medium'
          });
        }
        setIsLoading(false);
      }
    } else {
      setErrors(validation.errors);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row relative overflow-hidden">
      {/* Background image with reduced opacity overlay */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(/svgs/LoginBGright.svg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      <div
        className="absolute inset-0 z-[1]"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.55)' }}
        aria-hidden
      />
      <div className="w-full flex flex-col lg:flex-row min-h-screen relative z-10">
        {/* LEFT SIDE - illustration (desktop only), floating panel */}
        <div
          className={`hidden lg:flex lg:w-[45%] flex-col border border-[#DBDBDB] rounded-2xl lg:m-6 min-h-[calc(100vh-3rem)] px-6 pt-6 pb-8 lg:px-10 lg:pt-8 lg:pb-10 shadow-sm transition-all duration-700 ease-out ${
            mounted ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}
          style={{ backgroundColor: '#F9FAFB' }}
        >
          <div className="flex flex-col flex-1">
            <div className="mb-6">
              <img
                src={getLogoByDomainAndAlt().darkLogo}
                alt={getLogoByDomainAndAlt().alt}
                className="h-6 w-auto"
              />
            </div>
            <div className="flex-1 flex flex-col items-center justify-center">
              <img
                key={currentSlide}
                src={slides[currentSlide].image}
                alt={slides[currentSlide].title}
                className="w-full max-w-[420px] max-h-[420px] object-contain transition-opacity duration-500 ease-out"
              />
              <div className="mt-3 rounded-xl border border-[#DBDBDB] bg-white px-4 py-3.5 w-full max-w-[360px] transition-shadow duration-300 hover:shadow-md" style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                <h2 className="text-base font-semibold text-font-primary mb-1.5 leading-snug">
                  {slides[currentSlide].title}
                </h2>
                <p className="text-xs text-font-secondary leading-relaxed m-0">
                  {slides[currentSlide].description}
                </p>
              </div>
              <div className="flex justify-center gap-1.5 mt-2.5">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full border-none cursor-pointer transition-all duration-300 ease-out ${
                      index === currentSlide ? 'bg-primary scale-125' : 'bg-gray-200 hover:bg-gray-300'
                    }`}
                    aria-label={`Slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - form */}
        <div className="w-full lg:w-[50%] min-h-screen flex items-center justify-center px-4 py-6 lg:px-8 lg:py-6">
          <div
            className={`w-full max-w-md bg-white rounded-xl px-5 py-6 sm:px-8 sm:py-8 border border-[#DBDBDB] shadow-card transition-all duration-700 ease-out ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            } ${isLoading ? 'opacity-60 pointer-events-none' : ''}`}
          >
          {/* Logo for mobile */}
          <div className="mb-4 flex justify-center lg:hidden">
            <img
              src={getLogoByDomainAndAlt().darkLogo}
              alt={getLogoByDomainAndAlt().alt}
              className="h-5 w-auto"
            />
          </div>

          {/* Form Header */}
          <h1 className="text-2xl font-semibold text-font-primary mb-1.5 text-center">
          Sign in to your account
          </h1>
          <p className="text-xs text-font-secondary mb-5 text-center leading-normal">
           Please enter your email and password to sign in to your account.
          </p>

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-font-primary mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <Icon
                  icon="mdi:email-outline"
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
                />
                <input
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="user@email.com"
                  disabled={isLoading}
                  className={`w-full h-10 pl-9 pr-3 text-sm text-font-primary bg-light-background rounded-md outline-none box-border transition-all duration-200 ease-out focus:shadow-input-focus focus:border-primary ${
                    errors.email ? 'border border-error-border' : 'border border-gray-200'
                  }`}
                />
              </div>
              {errors.email && (
                <span className="text-error text-xs mt-1 block">
                  {errors.email[0]}
                </span>
              )}
            </div>

            {/* Password */}
            <div className="mb-1.5">
              <label className="block text-xs font-medium text-font-primary mb-1.5">
                Password
              </label>
              <div className="relative">
                <Icon
                  icon="mdi:lock-outline"
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  disabled={isLoading}
                  className={`w-full h-10 pl-9 pr-9 text-sm text-font-primary bg-light-background rounded-md outline-none box-border transition-all duration-200 ease-out focus:shadow-input-focus focus:border-primary ${
                    errors.password ? 'border border-error-border' : 'border border-gray-200'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer p-0 flex items-center"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon
                    icon={showPassword ? 'mdi:eye-off-outline' : 'mdi:eye-outline'}
                    className="w-4 h-4 text-gray-500"
                  />
                </button>
              </div>
              {errors.password && (
                <span className="text-error text-xs mt-1 block">
                  {errors.password[0]}
                </span>
              )}
            </div>

            {/* Forgot password - contact admin */}
            <div className="mb-4 flex justify-end">
              <button
                type="button"
                onClick={() =>
                  toast.info('Please contact your administrator to reset your password.', {
                    position: 'top-center',
                    autoClose: 5000,
                    className: 'bg-font-primary text-white text-sm rounded-xl shadow-lg',
                    progressClassName: 'bg-primary',
                    bodyClassName: 'font-medium',
                  })
                }
                className="text-xs text-primary hover:text-primary-hover font-medium transition-colors duration-200"
              >
                Forgot password?
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!formData.email || !formData.password || isLoading}
              className={`w-full h-10 text-sm font-medium tracking-wide text-white border-none rounded-md transition-all duration-300 ease-out ${
                formData.email && formData.password && !isLoading
                  ? 'bg-primary cursor-pointer hover:bg-primary-hover hover:shadow-md active:scale-[0.99]'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {isLoading ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Logging in...
                </span>
              ) : (
                'Log in'
              )}
            </button>
          </form>

          {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
            <div className="mt-4">
              <div id="googleSignInDiv" />
            </div>
          )}

          {!process.env.REACT_APP_GOOGLE_CLIENT_ID && (
            <div className="mt-4 text-xs text-font-secondary text-center">
              Google login is not configured.
            </div>
          )}

        </div>

        {/* Powered by HealthChip */}
        <div className="absolute bottom-6 right-6 flex items-center gap-2">
          <span className="text-sm text-gray-500">Powered by</span>
          <a
            href="https://healthchip.in/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block transition-opacity hover:opacity-80"
          >
            <img
              src="/images/logos/health chip logo .png"
              alt="HealthChip"
              className="h-7 w-auto"
            />
          </a>
        </div>
      </div>
    </div>
  </div>
  );
}

export default Login;
